// app/ has no slice, so no model/ to hold hooks — colocate the hook next to
// the component per spec §4.3 (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md).
import { fetchSupportGuideline } from '@/entities/support';
import { handleViewTutorialLater } from '@/entities/user';
import { auth, showMessageError } from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import type { WhatIsNewModalProps } from './whatIsNewModal.types';

/**
 * Backs the what-is-new release-notes popup with its guideline content and close actions.
 * Fetches the what-is-new support guideline on mount, keeps the video id or PDF URL in refs and
 * the entry in state, and wires the view-later and close buttons to the tutorial-later update.
 *
 * @param props - The willShowWhatNewPopup flag and the displayWhatNewPopup callback.
 * @returns The guideline state, the video id and PDF URL refs and the view-later and close handlers.
 */
export const useWhatIsNewModal = ({
  willShowWhatNewPopup,
  displayWhatNewPopup,
}: Pick<WhatIsNewModalProps, 'willShowWhatNewPopup' | 'displayWhatNewPopup'>) => {
  const IDVIDEO = useRef<any>(undefined);
  const URL_PDF = useRef<any>(undefined);

  const [state, setState] = useMergeState<{
    dataWhatNew: { url: string | null; type: string | null } | undefined;
  }>({
    dataWhatNew: undefined,
  });

  /**
   * Closes the what-is-new release notes popup and stops it reappearing. Persists the will-show flag as false when the popup was still pending, then hides the popup through the display callback.
   *
   * @returns Nothing.
   */
  const closeWhatNewPopUp = () => {
    if (willShowWhatNewPopup) {
      auth.saveWillShowWhatNewPopup(false);
    }
    displayWhatNewPopup(false);
  };

  /**
   * Persists the user's choice to see the tutorial later and optionally closes the release-notes popup. Compares the new preference with the stored one and only calls the backend and saves it when it changed, closing the popup when requested or otherwise clearing the will-show flag, and closing the popup as well when the update fails.
   *
   * @param newWillViewTutorialLater - The view-tutorial-later preference to persist.
   * @param willClose - Whether the release-notes popup should be closed afterwards.
   * @returns A promise resolving once the preference has been persisted and the popup state settled.
   */
  const updateViewTutorialLater = async (newWillViewTutorialLater: any, willClose: any) => {
    try {
      const oldWillViewTutorialLater = JSON.parse(auth.getWillViewTutorialLater());
      if (newWillViewTutorialLater !== oldWillViewTutorialLater) {
        await handleViewTutorialLater(newWillViewTutorialLater);

        auth.saveWillViewTutorialLater(newWillViewTutorialLater);
        if (willClose) {
          closeWhatNewPopUp();
        } else if (willShowWhatNewPopup) {
          auth.saveWillShowWhatNewPopup(false);
        }
      } else if (willClose) {
        closeWhatNewPopUp();
      }
    } catch (error) {
      console.error('Failed to update view tutorial later: ', error);
      closeWhatNewPopUp();
    }
  };

  /**
   * Closes the release-notes popup and opts into seeing the tutorial later. Delegates to the tutorial-later update with the preference set to true and the close flag set.
   *
   * @returns Nothing.
   */
  const onClickViewLater = () => {
    updateViewTutorialLater(true, true);
  };

  /**
   * Closes the release-notes popup without opting into viewing the tutorial later. Delegates to the tutorial-later update with the preference set to false and the close flag set.
   *
   * @returns Nothing.
   */
  const onClickClose = () => {
    updateViewTutorialLater(false, true);
  };

  /**
   * Loads the what-is-new support guideline content shown in the release-notes popup. Fetches the guideline and bails out when it is empty, extracts the YouTube video id from the URL for video entries or keeps the PDF URL otherwise, writes the entry into state, and surfaces any failure as an error message.
   *
   * @returns A promise resolving once the guideline entry or the error message has been stored.
   */
  const fetchData = async () => {
    try {
      const dataGuie = await fetchSupportGuideline();
      if (!dataGuie || !dataGuie.whatIsNew?.length) {
        return;
      }
      const dataWhatNew = dataGuie?.whatIsNew?.[0];
      if (!dataWhatNew) {
        return;
      }
      if (dataWhatNew.type === 'video' && dataWhatNew.url) {
        const arrayId = dataWhatNew.url.split('/');
        IDVIDEO.current = _.replace(arrayId[arrayId.length - 1], 'watch?v=', '');
      } else if (dataWhatNew.url) {
        URL_PDF.current = dataWhatNew.url;
      }
      setState({ dataWhatNew });
    } catch (error) {
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { state, IDVIDEO, URL_PDF, onClickViewLater, onClickClose };
};
